import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { api, assetUrl, isSignInExpired } from '../lib/api';
import { formatInTz } from '../lib/timezone';
import { reviewIdFromHash } from '../lib/routes';
import { BOOKING_STATUS, bookedInstructorIds, instructorSessionsPage, rebookPage } from '../lib/rebook';
import { useViewerTz } from '../hooks/useViewerTz';
// No mock data imports needed — booking section uses live API
import { Stars, Btn, Badge, InstructorCard } from '../components';
import { JoinLink } from '../components/JoinLink';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { ReviewModal } from '../components/ReviewModal';
import { ProofOfPaymentNote } from '../components/ProofOfPaymentNote';
import { ImageLightbox } from '../components/ImageLightbox';
import { MarkPaidButton } from '../components/MarkPaidButton';
import { PaymentSentBadge } from '../components/PaymentSentBadge';
import { PayBadge, PayButton } from '../components/PayButton';
import { showsPay } from '../lib/payments';
import { Avatar } from '../components/Avatar';
import { AddToCalendarButton, calendarErrorMessage, calendarIdFromHash, downloadCalendarFile } from '../components/AddToCalendarButton';
import { DashboardMenu, DashboardLogoutButton } from '../components/DashboardMenu';
import { DASHBOARD_TABS, tabLabel } from '../lib/dashboardTabs';
import { greetingFor } from '../lib/greeting';
import { MESSAGES_TAB, tabFromHash, useUnreadMessages, withMessagesLabel } from '../lib/messages';
import { MessageButton } from '../components/messages/MessageButton';
import { MessagesPanel } from '../components/messages/MessagesPanel';
import { fieldProps } from '../lib/a11y';
import { palette as T, alpha, type } from '../design';

// ropida/dashboard-student.jsx — Student dashboard (Terracotta Studio)


// Where the address goes once a review link (`#/dashboard-student?review=<id>`) has been used,
// so a reload does not reopen the review form.
const DASHBOARD_HASH = '#/dashboard-student';

// What a student reads when their bookings did not load (#505, #507).
const BOOKINGS_LOAD_FAILED = 'We could not load your bookings. Refresh the page to try again.';

// A blank display name used to be dropped from the save without a word (#345).
const NAME_MISSING = 'Enter your display name. Instructors see it on your bookings.';

const profileLabelStyle = { display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, fontWeight: 600, color: T.walnut, marginBottom: 6 };
const profileInputStyle = (invalid) => ({
  width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid ' + (invalid ? T.errorRed : T.border),
  fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box',
});

function StudentDashboard({ setPage }) {
  const isMobile = useIsMobile();
  const { user: authUser, updateProfile } = useAuth();
  // Render session times in the student's own saved timezone (labeled), not browser-local (TTT-14).
  const [viewerTz, setViewerTz] = useViewerTz();
  const { showSnackbar } = useSnackbar();
  // The review email links here with `?review=<bookingId>`: open Past and that session's review form.
  const [pendingReviewId, setPendingReviewId] = React.useState(() => reviewIdFromHash(window.location.hash));
  // The confirmation email links here with `?calendar=<bookingId>`: download that booking's calendar file (PA-4).
  const [pendingCalendarId, setPendingCalendarId] = React.useState(() => calendarIdFromHash(window.location.hash));
  // A link may name a tab (`?tab=messages`, the new message email); a review or calendar link opens bookings.
  const [section, setSection] = React.useState(() => (pendingReviewId || pendingCalendarId
    ? 'bookings'
    : tabFromHash(window.location.hash, DASHBOARD_TABS.student) || 'home'));
  // The conversation to open on the Messages tab, set when a Message on a booking starts one.
  const [openConversationId, setOpenConversationId] = React.useState(null);
  const [unreadMessages, setUnreadMessages] = useUnreadMessages();
  const [classTab, setClassTab] = React.useState(() => (pendingReviewId ? 'past' : 'upcoming'));
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  // `profileError` is form-level (the server's answer); `nameError` is read out with its field.
  const [profileError, setProfileError] = React.useState(null);
  const [nameError, setNameError] = React.useState('');
  const [reviewBooking, setReviewBooking] = React.useState(null);
  const [payDetailsFor, setPayDetailsFor] = React.useState(null); // booking id with payment panel open

  const handleCancelBooking = async (cls) => {
    if (!window.confirm(`Cancel your booking for ${formatInTz(cls.scheduledAt, viewerTz)}? Your instructor will be notified.`)) return;
    try {
      await api.patch(`/bookings/${cls.id}/status`, { status: 'cancelled' });
      showSnackbar('Booking cancelled.', { variant: 'success' });
      refreshData();
    } catch (err) {
      // An ended sign-in already took them to the login page with its own notice (#502).
      if (isSignInExpired(err)) return;
      showSnackbar('Failed to cancel: ' + (err.data?.message || err.message), { variant: 'error' });
    }
  };

  const refreshData = () =>
    api.get('/dashboard/student')
      .then(setData)
      .catch((err) => {
        setData(null);
        // An ended sign-in already took them to the login page with its own notice (#502). Any
        // other failure, a server problem while renewing the sign-in included, is said (#505).
        if (isSignInExpired(err)) return;
        console.error('[StudentDashboard] loading bookings failed', err);
        showSnackbar(BOOKINGS_LOAD_FAILED, { variant: 'error' });
      });

  // The loading message stays until the first answer arrives. Clearing it straight away showed
  // "no sessions" for a moment to students who have some.
  React.useEffect(() => {
    refreshData().finally(() => setLoading(false));
  }, []);

  // A review link opened while the dashboard is already showing changes only the hash.
  React.useEffect(() => {
    const onHashChange = () => {
      const calendarId = calendarIdFromHash(window.location.hash);
      if (calendarId) {
        setSection('bookings');
        setClassTab('upcoming');
        setPendingCalendarId(calendarId);
        return;
      }
      const tab = tabFromHash(window.location.hash, DASHBOARD_TABS.student);
      if (tab) {
        setSection(tab);
        window.history.replaceState(null, '', DASHBOARD_HASH);
        return;
      }
      const id = reviewIdFromHash(window.location.hash);
      if (!id) return;
      setSection('bookings');
      setClassTab('past');
      setPendingReviewId(id);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // A tab link has done its job once the tab shows, so a reload does not keep reopening it.
  React.useEffect(() => {
    if (tabFromHash(window.location.hash, DASHBOARD_TABS.student)) window.history.replaceState(null, '', DASHBOARD_HASH);
  }, []);

  // Message on a booking started or continued a conversation: show it on the Messages tab.
  const openConversation = (conversation) => {
    setOpenConversationId(conversation?.id || null);
    setSection(MESSAGES_TAB);
  };

  // Use the review link once the bookings are in. The id only picks among this student's own
  // past sessions, so a link for someone else's booking opens nothing.
  React.useEffect(() => {
    if (loading || !pendingReviewId || !data) return;
    const match = (data.pastClasses || []).find(b => b.id === pendingReviewId);
    if (match && !match.review) {
      setReviewBooking(match);
    } else if (match) {
      showSnackbar('You already reviewed this session.', { variant: 'info' });
    } else {
      showSnackbar('That session is not in your past sessions. Pick one from the list below.', { variant: 'info' });
    }
    setPendingReviewId(null);
    window.history.replaceState(null, '', DASHBOARD_HASH);
  }, [loading, pendingReviewId, data, showSnackbar]);

  // Use the calendar link once the bookings are in. The id only picks among this student's own
  // upcoming confirmed bookings; the server checks the same again before it sends the file.
  React.useEffect(() => {
    if (loading || !pendingCalendarId || !data) return;
    const match = (data.upcomingClasses || []).find(b => b.id === pendingCalendarId);
    if (match && match.status === BOOKING_STATUS.CONFIRMED) {
      downloadCalendarFile(match.id)
        .then(() => showSnackbar('Your calendar file is downloading. Open it to add the session to your calendar.', { variant: 'success' }))
        .catch((err) => {
          if (isSignInExpired(err)) return;
          showSnackbar(calendarErrorMessage(err), { variant: 'error' });
        });
    } else if (match) {
      showSnackbar('This booking is not confirmed yet. You can add it to your calendar once your instructor confirms it.', { variant: 'info' });
    } else {
      showSnackbar('That session is not in your upcoming sessions. Pick one from the list below.', { variant: 'info' });
    }
    setPendingCalendarId(null);
    window.history.replaceState(null, '', DASHBOARD_HASH);
  }, [loading, pendingCalendarId, data, showSnackbar]);

  const student = data?.student || {};
  const studentUser = student?.user || {};
  const upcoming = data?.upcomingClasses || [];
  const past = data?.pastClasses || [];
  const nextClass = upcoming[0];

  // The dashboard endpoint only carries active/completed bookings. The full list feeds the
  // Cancelled tab (students can cancel) and the rebook section (who this student booked before).
  // Null until it first arrives, so the rebook section never flashes its empty message.
  const [myBookings, setMyBookings] = React.useState(null);
  const [myBookingsFailed, setMyBookingsFailed] = React.useState(false);
  React.useEffect(() => {
    if (section !== 'bookings' && section !== 'book') return;
    api.get('/bookings/my')
      .then(d => {
        setMyBookings(d?.bookings || []);
        setMyBookingsFailed(false);
      })
      .catch((err) => {
        setMyBookings([]);
        // An ended sign-in already took them to the login page with its own notice (#502). Any
        // other failure, a server problem while renewing the sign-in included, is said (#505).
        if (isSignInExpired(err)) return;
        console.error('[StudentDashboard] loading all bookings failed', err);
        setMyBookingsFailed(true);
      });
  }, [section, data]);
  const cancelledBookings = React.useMemo(() => (myBookings || [])
    .filter(b => b.status === BOOKING_STATUS.CANCELLED)
    .map(b => ({ ...b, instructorName: b.instructor?.user?.name || 'Instructor' })), [myBookings]);
  const bookedIds = React.useMemo(() => bookedInstructorIds(myBookings), [myBookings]);

  // The instructor cards for the rebook section. Null until they arrive, like `myBookings`.
  const [instructorsList, setInstructorsList] = React.useState(null);
  const [instructorsFailed, setInstructorsFailed] = React.useState(false);
  React.useEffect(() => {
    api.get('/instructors')
      .then(d => {
        setInstructorsList(d?.instructors || []);
        setInstructorsFailed(false);
      })
      .catch((err) => {
        setInstructorsList([]);
        console.error('[StudentDashboard] loading instructors failed', err);
        setInstructorsFailed(true);
      });
  }, []);
  // The rebook section: only instructors this student holds a confirmed or completed booking with.
  // It speaks only once both lists are in; a failure in either says so instead of "none yet".
  const rebookFailed = myBookingsFailed || instructorsFailed;
  const rebookLoaded = myBookings !== null && instructorsList !== null;
  const yourInstructors = (instructorsList || []).filter(i => i.status === 'active' && bookedIds.has(i.id));

  // Compute countdown to next class
  const countdown = React.useMemo(() => {
    if (!nextClass?.scheduledAt) return { days: 0, hours: 0, mins: 0 };
    const diff = new Date(nextClass.scheduledAt) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, mins: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      mins: Math.floor((diff / (1000 * 60)) % 60),
    };
  }, [nextClass]);

  // Profile form state
  const [profileForm, setProfileForm] = React.useState({
    name: '',
    timezone: 'Asia/Manila',
    avatarUrl: '',
    level: '',
    targetExam: '',
  });

  React.useEffect(() => {
    if (authUser) {
      setProfileForm({
        name: authUser.name || '',
        timezone: authUser.timezone || 'Asia/Manila',
        avatarUrl: authUser.avatarUrl || '',
        level: authUser.student?.level || '',
        targetExam: authUser.student?.targetExam || '',
      });
    }
  }, [authUser]);

  const handleProfileChange = (field, value) => {
    setProfileError(null);
    if (field === 'name') setNameError('');
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const nameField = fieldProps('student-profile-name', nameError);
  const levelField = fieldProps('student-profile-level', '');
  const examField = fieldProps('student-profile-exam', '');

  const handleSaveProfile = async () => {
    setProfileError(null);
    if (!profileForm.name.trim()) {
      setNameError(NAME_MISSING);
      document.getElementById(nameField.inputProps.id)?.focus();
      return;
    }
    setSaving(true);
    try {
      const payload = { ...profileForm, name: profileForm.name.trim() };
      await updateProfile(payload);
      const refreshed = await api.get('/dashboard/student');
      setData(refreshed);
      showSnackbar('Profile updated successfully.', { variant: 'success' });
    } catch (err) {
      // The sign-in ended: the app shows the login page's notice, so the server's own
      // "Please log in to continue." stays off this page (#502).
      if (isSignInExpired(err)) return;
      const msg = err.data?.message || err.message || 'Failed to save profile';
      setProfileError(msg);
      showSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setProfileError(null);
    try {
      const res = await api.post('/profile/upload-avatar', formData);
      setProfileForm(prev => ({ ...prev, avatarUrl: res.url }));
      await updateProfile({ avatarUrl: res.url });
      const refreshed = await api.get('/dashboard/student');
      setData(refreshed);
      showSnackbar('Avatar uploaded successfully.', { variant: 'success' });
    } catch (err) {
      if (isSignInExpired(err)) return;
      const msg = err.data?.message || err.message || 'Failed to upload avatar';
      setProfileError(msg);
      showSnackbar(msg, { variant: 'error' });
    }
  };

  const navItems = withMessagesLabel(DASHBOARD_TABS.student, unreadMessages);

  const SideNav = () => (
    <div style={{ width: 220, flexShrink: 0, background: T.white, borderRight: '1px solid ' + T.border, minHeight: 'calc(100vh - 64px)', padding: '24px 0' }}>
      <div style={{ padding: '0 16px 20px', borderBottom: '1px solid ' + T.border, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={studentUser.name || 'Student'} src={assetUrl(studentUser.avatarUrl)} />
          <div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.espresso, margin: 0 }}>{studentUser.name || 'Student'}</p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: 0 }}>Student</p>
          </div>
        </div>
      </div>
      {navItems.map(item => (
        <button key={item.id} onClick={() => setSection(item.id)}
          style={{
            width: '100%', padding: '11px 20px', border: 'none',
            background: section === item.id ? T.ivory : 'transparent',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
            textAlign: 'left', borderRight: section === item.id ? '3px solid ' + T.terracotta : '3px solid transparent',
            transition: 'all 0.15s'
          }}>
          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: section === item.id ? 600 : 400, fontSize: type.body, color: section === item.id ? T.terracotta : T.walnut }}>{item.label}</span>
        </button>
      ))}
      <div style={{ padding: '20px 16px 0', borderTop: '1px solid ' + T.border, marginTop: 12 }}>
        <button onClick={() => setPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>← Back to site</button>
        <DashboardLogoutButton setPage={setPage} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ paddingTop: 0, display: 'flex', minHeight: '100vh', background: T.ivory, alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading dashboard…</p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 0, display: 'flex', minHeight: '100vh', background: T.ivory, flexDirection: isMobile ? 'column' : 'row' }}>
      {!isMobile && <SideNav />}
      <DashboardMenu tabs={navItems} active={section} onSelect={setSection} setPage={setPage} />
      <div style={{ flex: 1, padding: isMobile ? '16px' : '32px', overflowY: 'auto', maxHeight: isMobile ? 'none' : 'calc(100vh - 64px)', paddingBottom: '32px' }}>

        {/* ── Home ── */}
        {section === 'home' && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 32, color: T.espresso, margin: '0 0 4px' }}>{greetingFor(new Date(), authUser?.timezone)}, {studentUser.name || authUser?.name || 'there'}</h1>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, margin: 0 }}>Ready to practice today?</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: isMobile ? 12 : 20, marginBottom: 24 }}>
              {/* Next class */}
              <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2', background: 'linear-gradient(135deg,' + T.terracotta + ',' + T.terracottaDark + ')', borderRadius: 20, padding: '28px 28px', color: T.white, position: 'relative', overflow: 'hidden' }}>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, fontWeight: 600, margin: '0 0 8px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 1 }}>Next session</p>
                <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, margin: '0 0 16px' }}>{nextClass ? `Session with ${nextClass.instructorName}` : 'No upcoming sessions'}</p>
                {nextClass && (
                  <div style={{ display: 'flex', gap: 16 }}>
                    {[['Days', countdown.days], ['Hours', countdown.hours], ['Mins', countdown.mins]].map(([l, v]) => (
                      <div key={l} style={{ textAlign: 'center' }}>
                        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 32, lineHeight: 1 }}>{String(v).padStart(2, '0')}</div>
                        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, opacity: 0.8, marginTop: 2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                )}
                {nextClass && (
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, margin: '12px 0 0', opacity: 0.85 }}>
                    {formatInTz(nextClass.scheduledAt, viewerTz)} · {nextClass.session?.title || nextClass.sessionType}
                  </p>
                )}
              </div>

              {/* Upcoming sessions widget (session packs removed in TTT-58 — no more /10 pack gauge) */}
              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24, textAlign: 'center' }}>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.label, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 12px' }}>Upcoming sessions</p>
                <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 44, color: T.espresso, lineHeight: 1, margin: '0 0 6px' }}>{upcoming.length}</p>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: '0 0 16px' }}>{upcoming.length === 1 ? 'session booked' : 'sessions booked'}</p>
                <Btn size="sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setSection('book')}>Book next →</Btn>
              </div>
            </div>

            {/* Quick actions */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr', gap: isMobile ? 8 : 16, marginBottom: 24, maxWidth: isMobile ? 'none' : 320 }}>
              <button onClick={() => setSection('book')} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '20px 16px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = T.ivory; e.currentTarget.style.borderColor = T.terracotta; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.white; e.currentTarget.style.borderColor = T.border; }}>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso }}>Find a session</span>
              </button>
            </div>

            {/* Upcoming classes preview */}
            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: 0 }}>Upcoming sessions</h3>
                <button onClick={() => setSection('bookings')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.terracotta }}>See all →</button>
              </div>
              {upcoming.length === 0 && (
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '20px 0' }}>No upcoming sessions. Book one now!</p>
              )}
              {upcoming.slice(0, 3).map(cls => (
                <div key={cls.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid ' + T.border }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: T.ivory, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: type.body, fontWeight: 700, color: T.terracotta, fontFamily: "'Lora', serif" }}>C</div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{cls.session?.title || cls.sessionType} · {cls.instructorName}</p>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: 0 }}>{formatInTz(cls.scheduledAt, viewerTz)} · {cls.duration || 60} min</p>
                    {cls.instructorEmail && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.terracotta, margin: '2px 0 0' }}>{cls.instructorEmail}</p>}
                  </div>
                  {cls.status === 'pending' ? (
                    showsPay(cls) ? <PayBadge booking={cls} /> : <PaymentSentBadge booking={cls} />
                  ) : (
                    <JoinLink meetingLink={cls.meetingLink} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── My Bookings ── */}
        {section === 'bookings' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 24px' }}>
              <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: 0 }}>My Bookings</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood }}>Times in your timezone:</span>
                <TimezoneSelect value={viewerTz} onChange={setViewerTz}
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.espresso, padding: '6px 8px', borderRadius: 8, border: '1px solid ' + T.border, background: T.white, outline: 'none', maxWidth: 230 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: T.white, borderRadius: 12, padding: 4, border: '1px solid ' + T.border, width: 'fit-content' }}>
              {['upcoming', 'past', 'cancelled'].map(t => (
                <button key={t} onClick={() => setClassTab(t)} style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: classTab === t ? T.terracotta : 'transparent', color: classTab === t ? T.white : T.walnut, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(classTab === 'upcoming' ? upcoming : classTab === 'past' ? past : cancelledBookings).map(cls => (
                <div key={cls.id} data-testid="booking-card" style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '18px 20px', opacity: classTab === 'cancelled' ? 0.75 : 1 }}>
                  {/* Wraps on a 390 px phone, so the buttons move under the booking instead of past the card's edge. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: classTab === 'upcoming' ? T.ivory : T.warmGrey, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: type.body, fontWeight: 700, color: T.terracotta, fontFamily: "'Lora', serif" }}>{classTab === 'upcoming' ? 'U' : classTab === 'past' ? 'P' : '✕'}</div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: '0 0 3px' }}>{cls.session?.title || cls.sessionType || 'Private 1-on-1'} · {cls.instructorName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0 }}>{formatInTz(cls.scheduledAt, viewerTz)} · {cls.duration || 60} min · ₱{(cls.price ?? 0).toLocaleString()}</p>
                      {cls.instructorEmail && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.terracotta, margin: '2px 0 0' }}>{cls.instructorEmail}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {classTab === 'upcoming' && <>
                        {cls.status === 'pending' && showsPay(cls) ? (
                          // JTP-5: Pay only. No instructor payment instructions, no screenshot step.
                          <PayBadge booking={cls} />
                        ) : cls.status === 'pending' ? (
                          <>
                            <PaymentSentBadge booking={cls} awaitingLabel="Awaiting payment confirmation" />
                            {(cls.session?.paymentInstructions || cls.session?.paymentQrUrl) && (
                              <Btn size="sm" variant="ghost" onClick={() => setPayDetailsFor(payDetailsFor === cls.id ? null : cls.id)}>
                                {payDetailsFor === cls.id ? 'Hide payment details' : 'How to pay'}
                              </Btn>
                            )}
                          </>
                        ) : (
                          <>
                            <JoinLink meetingLink={cls.meetingLink} />
                            <AddToCalendarButton booking={cls} />
                          </>
                        )}
                        <Btn size="sm" variant="ghost" onClick={() => handleCancelBooking(cls)}>Cancel</Btn>
                      </>}
                      {classTab === 'past' && cls.review && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          <span role="img" aria-label={`You rated this session ${cls.review.rating} out of 5`}>
                            <Stars rating={cls.review.rating} size={13} />
                          </span>
                          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood }}>Your rating</span>
                        </div>
                      )}
                      {classTab === 'past' && !cls.review && (
                        <Btn size="sm" onClick={() => setReviewBooking(cls)}>Leave a review</Btn>
                      )}
                      {classTab === 'past' && rebookPage(cls) && (
                        <Btn size="sm" variant="ghost" onClick={() => setPage(rebookPage(cls))}>Book again</Btn>
                      )}
                      {classTab === 'cancelled' && <Badge variant="default">Cancelled</Badge>}
                      <MessageButton bookingId={cls.id} otherName={cls.instructorName || 'your instructor'} onStarted={openConversation} />
                    </div>
                  </div>
                  {/* PA-1: its own row, so the hint and messages have room on a 390 px phone. */}
                  {classTab === 'upcoming' && cls.status === 'pending' && (
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                      {showsPay(cls)
                        ? <PayButton booking={cls} />
                        : <MarkPaidButton booking={cls} onMarked={refreshData} />}
                    </div>
                  )}
                  {classTab === 'upcoming' && cls.status === 'pending' && payDetailsFor === cls.id && (
                    <div style={{ marginTop: 12, marginLeft: 68, background: T.ivory, borderRadius: 12, border: '1px solid ' + T.border, padding: '14px 16px' }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.label, color: T.espresso, margin: '0 0 6px' }}>How to pay</p>
                      {cls.session?.paymentInstructions && (
                        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{cls.session.paymentInstructions}</p>
                      )}
                      {cls.session?.paymentQrUrl && (
                        <ImageLightbox src={assetUrl(cls.session.paymentQrUrl)} alt="Payment QR code" thumbStyle={{ maxWidth: 180, width: '100%', borderRadius: 10, border: '1px solid ' + T.border, marginTop: 10 }} />
                      )}
                      <ProofOfPaymentNote email={cls.instructorEmail} />
                    </div>
                  )}
                </div>
              ))}
              {/* RV-11.2: a failed load says so; "no cancelled bookings" only once the list arrived. */}
              {(classTab === 'cancelled' && myBookingsFailed) && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <p role="alert" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed }}>{BOOKINGS_LOAD_FAILED}</p>
                </div>
              )}
              {(classTab === 'cancelled' && !myBookingsFailed && myBookings !== null && cancelledBookings.length === 0) && (
                <div style={{ textAlign: 'center', padding: '60px 0', color: T.driftwood }}>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body }}>You have no cancelled bookings.</p>
                </div>
              )}
              {(classTab === 'past' && past.length === 0) && (
                <div style={{ textAlign: 'center', padding: '60px 0', color: T.driftwood }}>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, margin: '0 0 12px' }}>
                    You have no past sessions yet. After a session ends, it shows here so you can review your instructor.
                  </p>
                  <a href="#/marketplace" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, fontWeight: 600, color: T.terracotta }}>
                    Find a session on the marketplace
                  </a>
                </div>
              )}
              {(classTab === 'upcoming' && upcoming.length === 0) && (
                <div style={{ textAlign: 'center', padding: '60px 0', color: T.driftwood }}>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body }}>No upcoming sessions. Book one now!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Book (the rebook section) ── */}
        {section === 'book' && (
          <div>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 8px' }}>{tabLabel(navItems, 'book')}</h2>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, margin: '0 0 24px' }}>Book a seat with an instructor you know, or find someone new on the marketplace.</p>
            <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.walnut, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 16px' }}>Your instructors</h3>
            {rebookFailed && (
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>
                We could not load your instructors. Refresh the page to try again.
              </p>
            )}
            {!rebookFailed && rebookLoaded && yourInstructors.length === 0 && (
              <div style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '32px 20px', textAlign: 'center' }}>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, margin: '0 0 12px' }}>
                  Instructors you book show here, so you can book them again in one tap.
                </p>
                <a href="#/marketplace" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, fontWeight: 600, color: T.terracotta }}>
                  Find an instructor on the marketplace
                </a>
              </div>
            )}
            {!rebookFailed && yourInstructors.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
                {yourInstructors.map(ins => (
                  <InstructorCard key={ins.id} instructor={ins} onClick={() => setPage(instructorSessionsPage(ins))} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Profile ── */}
        {section === 'profile' && (
          <div>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 24px' }}>Your Profile</h2>
            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 28, maxWidth: 640 }}>
              {/* Avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <Avatar name={profileForm.name || studentUser.name} src={assetUrl(profileForm.avatarUrl)} size={64} label="Your profile photo" />
                <div>
                  <label style={{ display: 'inline-block', padding: '8px 16px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, cursor: 'pointer', background: T.ivory }}>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
                    Upload photo
                  </label>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '6px 0 0' }}>JPG, PNG up to 5MB</p>
                </div>
              </div>

              {/* Name */}
              <div style={{ marginBottom: 16 }}>
                <label {...nameField.labelProps} style={profileLabelStyle}>Display name</label>
                <input {...nameField.inputProps} value={profileForm.name} onChange={e => handleProfileChange('name', e.target.value)} required style={profileInputStyle(nameError)} />
                {nameError && <p {...nameField.errorProps} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: '6px 0 0' }}>{nameError}</p>}
              </div>

              {/* Timezone */}
              <div style={{ marginBottom: 16 }}>
                {/* The timezone picker names itself "Timezone"; this heading is not a <label>,
                    because the picker's input lives inside a shared component. */}
                <span style={profileLabelStyle}>Timezone</span>
                <TimezoneSelect value={profileForm.timezone} onChange={(tz) => handleProfileChange('timezone', tz)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, background: T.white, color: T.espresso, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box' }} />
              </div>

              {/* Level */}
              <div style={{ marginBottom: 16 }}>
                <label {...levelField.labelProps} style={profileLabelStyle}>Current level</label>
                <input {...levelField.inputProps} value={profileForm.level} onChange={e => handleProfileChange('level', e.target.value)} placeholder="e.g. N5, Beginner" style={profileInputStyle(false)} />
              </div>

              {/* Target Exam */}
              <div style={{ marginBottom: 24 }}>
                <label {...examField.labelProps} style={profileLabelStyle}>Target exam</label>
                <input {...examField.inputProps} value={profileForm.targetExam} onChange={e => handleProfileChange('targetExam', e.target.value)} placeholder="e.g. JLPT N3" style={profileInputStyle(false)} />
              </div>

              {profileError && (
                <div role="alert" style={{ background: alpha('errorRed', 'tint'), borderRadius: 12, border: '1px solid ' + alpha('errorRed', 'selection'), padding: '12px 16px', marginBottom: 16 }}>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{profileError}</p>
                </div>
              )}
              <Btn onClick={handleSaveProfile} disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</Btn>
            </div>
          </div>
        )}

        {/* ── Messages ── */}
        {section === MESSAGES_TAB && (
          <MessagesPanel key={openConversationId || 'list'} initialConversationId={openConversationId} onUnreadChange={setUnreadMessages} />
        )}

        {reviewBooking && (
          <ReviewModal
            booking={reviewBooking}
            onClose={() => setReviewBooking(null)}
            onSubmitted={() => {
              setReviewBooking(null);
              refreshData();
            }}
          />
        )}
      </div>
    </div>
  );
}

export { StudentDashboard };
